/* xrLogShipper — server-side XR log channel.
 *
 * Wraps `console.log/warn/error` once (idempotent) and POSTs every line
 * starting with `[XR]` (or containing the marker) to `/api/xrlog.php`.
 * The PHP endpoint appends to `/api/xrlog.txt` on the cPanel host so the
 * developer can read what the headset actually saw via SSH (the only
 * viable channel — the user can't read browser console while wearing
 * the Quest 3, and clipboard copy out of VR is brittle).
 *
 * Batches 50 entries OR 2 seconds, whichever comes first; flushes on
 * `pagehide` via `navigator.sendBeacon` so a power-cycle doesn't lose
 * the last few critical lines. Idempotent global install — safe to
 * call multiple times. */
const SHIP_FLAG = "__knaadXRLogShipperInstalled";
const ENDPOINT = "/api/xrlog.php";
const BATCH_MAX = 50;
const FLUSH_MS = 2000;
function isXRMessage(args) {
    if (args.length === 0)
        return false;
    const first = args[0];
    if (typeof first !== "string")
        return false;
    return first.startsWith("[XR") || first.includes("[XR]") || first.includes("[XR-");
}
function formatArgs(args) {
    return args
        .map((a) => {
        if (typeof a === "string")
            return a;
        if (a === null)
            return "null";
        if (a === undefined)
            return "undefined";
        try {
            return JSON.stringify(a);
        }
        catch {
            return String(a);
        }
    })
        .join(" ");
}
function ship(entries) {
    if (entries.length === 0)
        return;
    const session = `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
    const body = JSON.stringify({ session, entries });
    // Prefer fetch; fall back to sendBeacon for unload.
    try {
        if (typeof fetch !== "undefined") {
            fetch(ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body,
                // keepalive lets the request survive a page navigation
                keepalive: true
            }).catch(() => { });
        }
        else if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            navigator.sendBeacon(ENDPOINT, body);
        }
    }
    catch { /* never crash the page on a log ship failure */ }
}
export function installXRLogShipper() {
    if (typeof window === "undefined")
        return;
    const g = window;
    if (g[SHIP_FLAG])
        return;
    g[SHIP_FLAG] = true;
    g.__knaadXRLogBuffer = [];
    g.__knaadXRLogSubscribers = g.__knaadXRLogSubscribers ?? [];
    const startedAt = performance.now();
    const buffer = g.__knaadXRLogBuffer;
    let timer = null;
    const flush = () => {
        if (buffer.length === 0)
            return;
        const out = buffer.splice(0, buffer.length);
        ship(out);
    };
    const enqueue = (level, args) => {
        if (!isXRMessage(args))
            return;
        const entry = {
            t: Math.floor(performance.now() - startedAt),
            level,
            msg: formatArgs(args)
        };
        buffer.push(entry);
        // Notify subscribers (e.g. in-VR HUD).
        const subs = g.__knaadXRLogSubscribers;
        if (subs) {
            for (const s of subs) {
                try {
                    s(entry);
                }
                catch { /* never let a subscriber break the chain */ }
            }
        }
        if (buffer.length >= BATCH_MAX) {
            flush();
        }
        else if (!timer) {
            timer = setTimeout(() => { timer = null; flush(); }, FLUSH_MS);
        }
    };
    const wrap = (level, original) => {
        return (...args) => {
            try {
                enqueue(level, args);
            }
            catch { /* never crash on capture */ }
            original.apply(console, args);
        };
    };
    console.log = wrap("log", console.log.bind(console));
    console.info = wrap("info", console.info.bind(console));
    console.warn = wrap("warn", console.warn.bind(console));
    console.error = wrap("error", console.error.bind(console));
    // Flush on page hide (covers Quest power-cycle and tab close).
    window.addEventListener("pagehide", () => {
        if (buffer.length === 0)
            return;
        const session = `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
        const body = JSON.stringify({ session, entries: buffer.splice(0, buffer.length), reason: "pagehide" });
        try {
            if (navigator.sendBeacon) {
                navigator.sendBeacon(ENDPOINT, body);
            }
        }
        catch { /* best-effort */ }
    });
    // First marker so we know shipping is alive on the server log.
    console.log("[XR] log-shipper installed", new Date().toISOString());
}
/** Subscribe to every captured XR entry. Used by the in-VR HUD. */
export function subscribeXRLog(fn) {
    if (typeof window === "undefined")
        return () => undefined;
    const g = window;
    g.__knaadXRLogSubscribers = g.__knaadXRLogSubscribers ?? [];
    g.__knaadXRLogSubscribers.push(fn);
    return () => {
        if (!g.__knaadXRLogSubscribers)
            return;
        g.__knaadXRLogSubscribers = g.__knaadXRLogSubscribers.filter((s) => s !== fn);
    };
}
/** Read the in-memory buffer (used by clipboard/HUD as a quick snapshot). */
export function getXRLogSnapshot() {
    if (typeof window === "undefined")
        return [];
    const g = window;
    return (g.__knaadXRLogBuffer ?? []).slice();
}
